// src/pages/CalendarPage/EventDetailsModal.jsx
import React from "react";
import { FaTimes, FaUsers, FaCalendarAlt, FaMapMarkerAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Button from "./../../components/Button/Button";

const EventDetailsModal = ({ event, isOpen, onClose, userRole }) => {
  const navigate = useNavigate();

  if (!isOpen || !event) return null;

  const goToEventPage = (eventId) => {
    navigate(`/convocations-admin/${eventId}`);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return isNaN(date)
      ? ""
      : date.toLocaleDateString("fr-FR", {
          day: "numeric",
          month: "long",
          year: "numeric",
        });
  };

  const start = formatDate(event.start);
  const end =
    event.end && event.end !== "0000-00-00" ? formatDate(event.end) : "";

  const categories = event.cate_event
    ? event.cate_event.split(",").map((c) => c.trim())
    : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-4xl mx-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-700/80 shadow-2xl overflow-hidden">
        {/* Bandeau haut */}
        <div className="h-1.5 bg-gradient-to-r from-primary via-indigo-500 to-cyan-400" />

        {/* Header */}
        <div className="flex items-start justify-between px-4 md:px-6 py-3 border-b border-slate-200/80 dark:border-slate-700/80 bg-slate-50/80 dark:bg-slate-900/90">
          <div className="pr-6">
            <h3 className="text-base md:text-lg font-bold text-slate-900 dark:text-white">
              {event.titre}
            </h3>
            <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-slate-500 dark:text-slate-300">
              {event.type_event && (
                <span className="inline-flex px-2 py-0.5 rounded-full bg-primary/10 text-primary font-semibold">
                  {event.type_event}
                </span>
              )}
              {event.level_event && (
                <span className="inline-flex px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800">
                  Niveau : {event.level_event}
                </span>
              )}
              {categories.length > 0 && (
                <span className="inline-flex px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800">
                  {categories.join(" · ")}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-slate-200/80 dark:hover:bg-slate-700/80 text-slate-600 dark:text-slate-200 transition-colors"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        {/* Contenu */}
        <div className="flex flex-col md:flex-row max-h-[75vh]">
          {/* Texte */}
          <div className="flex-1 px-4 md:px-6 py-4 md:py-5 overflow-y-auto text-sm text-slate-700 dark:text-slate-200 space-y-4">
            {/* Dates & lieu */}
            <div className="space-y-1">
              {start && (
                <div className="flex items-center gap-2">
                  <FaCalendarAlt className="text-slate-400 text-xs" />
                  <span>
                    <span className="font-semibold">Date : </span>
                    {start}
                    {end && ` – ${end}`}
                  </span>
                </div>
              )}
              {event.lieu_event && (
                <div className="flex items-center gap-2">
                  <FaMapMarkerAlt className="text-slate-400 text-xs" />
                  <span>
                    <span className="font-semibold">Lieu : </span>
                    {event.lieu_event}
                  </span>
                </div>
              )}
            </div>

            {/* Description */}
            {event.description && (
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 mb-1">
                  Description
                </p>
                <div
                  className="prose prose-sm max-w-none text-slate-700 dark:text-slate-200 prose-a:text-primary prose-ul:list-disc prose-ul:pl-5 dark:prose-invert"
                  dangerouslySetInnerHTML={{ __html: event.description }}
                />
              </div>
            )}

            {/* Horaires */}
            {event.horaire_event && (
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 mb-1">
                  Horaires / programme
                </p>
                <div
                  className="text-sm text-slate-700 dark:text-slate-200"
                  dangerouslySetInnerHTML={{ __html: event.horaire_event }}
                />
              </div>
            )}

            {/* Lien agenda */}
            {event.agenda_url && (
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 mb-1">
                  Ajouter à votre agenda
                </p>
                <a
                  href={event.agenda_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm text-primary hover:text-primary/80 font-semibold"
                >
                  Cliquer ici pour ajouter l’événement à votre agenda
                </a>
              </div>
            )}

            {/* Bouton admin */}
            {(userRole === "Administrateur" || userRole === "Bureau") && (
              <div className="pt-2">
                <Button
                  text="Voir les acteurs inscrits"
                  onClick={() => goToEventPage(event.id)}
                  icon={FaUsers}
                  className="w-full justify-center gap-2"
                />
              </div>
            )}
          </div>

          {/* Photo */}
          {event.photo_url && (
            <div className="w-full md:w-1/3 border-t md:border-t-0 md:border-l border-slate-200/80 dark:border-slate-700/80 bg-slate-50 dark:bg-slate-900/90 flex items-center justify-center p-4">
              <img
                src={event.photo_url}
                alt="Event visuel"
                className="max-h-[260px] w-full object-cover rounded-xl shadow-md"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventDetailsModal;
